import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  Input,
  Modal,
  ModalBody,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import TableContainer from "../../Components/Common/TableContainer";

import Loader from "../../Components/Common/Loader";

import {
  adminGetAllMembers,
  adminGetAllMembersExcel,
  adminGetComboWorkLocation,
  adminResetMemberPassword,
} from "helpers/backend_helpers";
import { formatDateTime, memberCategory } from "helpers/globalFunction";
import { toast } from "react-toastify";
import { useClipboard } from "use-clipboard-copy";
import Select from "react-select";
import AddModalMember from "./AddModalMember";
import DeleteModalMember from "./DeleteModalMember";
import UpdateModalMember from "./UpdateModalMember";
import DataUploadModalMember from "./DataUploadModalMember";
import PhotoUploadModalMember from "./PhotoUploadModalMember";
import SearchableSelect from "Components/Common/SearchableSelect";

const MemberList = () => {
  const [tableData, setTableData] = useState<any>([]);
  const [data, setData] = useState<any>({});
  const [sort, setSort] = useState("createTime");
  const [dir, setDir] = useState(-1);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [addModal, setAddModal] = useState<boolean>(false);
  const [updateModal, setUpdateModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [resetModal, setResetModal] = useState<boolean>(false);
  const [passwordModal, setPasswordModal] = useState<boolean>(false);
  const [uploadDataModal, setUploadDataModal] = useState<boolean>(false);
  const [photoModal, setPhotoModal] = useState<boolean>(false);

  const [newPassword, setNewPassword] = useState<string>("");
  const clipboard = useClipboard({
    copiedTimeout: 750,
  });

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string>("");
  const [filterId, setFilterId] = useState<string>("");
  const [filterWorkLocation, setFilterWorkLocation] = useState<any>(null);
  const [searchName, setSearchName] = useState<string>("");
  const [searchId, setSearchId] = useState<string>("");

  const [isLoading, setIsLoading] = useState(false);

  const [comboLocation, setComboLocation] = useState<any>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchName(filterName);
      setSearchId(filterId);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [filterName, filterId]);

  const getData = () => {
    setLoading(true);
    let query: any = {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sort: sort,
      dir: dir,
    };

    if (searchName) {
      query.name = searchName;
    }

    if (searchId) {
      query.noId = searchId;
    }

    if (filterWorkLocation) {
      query.workLocationId = filterWorkLocation.value;
    }

    adminGetAllMembers(query)
      .then((response: any) => {
        setTableData(
          response.content.map((el: any, index: any) => {
            let dibuat = formatDateTime(el.createTime);
            let diperbarui = formatDateTime(el.updateTime);
            return {
              ...el,
              index: index + pagination.pageIndex * pagination.pageSize + 1,
              dibuat,
              diperbarui,
            };
          }),
        );
        setRowCount(response.totalElements);
        setTotalPages(response.totalPages);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResetPassword = async () => {
    setLoading(true);
    await adminResetMemberPassword(data._id, {})
      .then((response: any) => {
        setResetModal(false);
        setNewPassword(response.newPassword);
        toast.success(
          <div>
            Password <b>{data?.name}</b> berhasil di reset!
          </div>,
        );
        setPasswordModal(true);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const downloadData = async () => {
    setIsLoading(true);
    await adminGetAllMembersExcel()
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Data Karyawan.xlsx`);
        document.body.appendChild(link);
        link.click();
        toast.success("Unduh data karyawan berhasil");
      })
      .catch(() => {
        toast.error("Gagal!");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const copyText = () => {
    if (!newPassword) return;
    clipboard.copy(newPassword);
    toast.success("Password berhasil disalin");
  };

  const getComboLocation = async () => {
    await adminGetComboWorkLocation()
      .then((response: any) => {
        setComboLocation(
          response.map((val: any) => {
            return { value: val._id, label: val.name };
          }),
        );
      })
      .catch(() => {});
  };

  const handleResetFilter = () => {
    setFilterName("");
    setSearchName("");
    setFilterId("");
    setSearchId("");
    setFilterWorkLocation(null);
  };

  const handleToggleFilter = () => {
    if (showFilter) {
      handleResetFilter();
    }
    setShowFilter(!showFilter);
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "index",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Nama",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "No. Reg",
        accessorKey: "noId",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Tipe",
        accessorKey: "category",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => memberCategory(cell.getValue()),
      },
      {
        header: "Lokasi Kerja",
        accessorKey: "workLocationName",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Dibuat",
        accessorKey: "dibuat",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Diperbarui",
        accessorKey: "diperbarui",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Aksi",
        cell: (cellProps: any) => {
          return (
            <div className="hstack gap-2">
              <UncontrolledTooltip
                target={`edit-${cellProps.row.original._id}`}
              >
                Edit
              </UncontrolledTooltip>
              <button
                id={`edit-${cellProps.row.original._id}`}
                className="btn btn-sm btn-soft-warning edit-list"
                onClick={() => {
                  setUpdateModal(true);
                  setData(cellProps.row.original);
                }}
              >
                <i className="ri-pencil-fill align-bottom" />
              </button>

              <UncontrolledTooltip
                target={`resetPw-${cellProps.row.original._id}`}
              >
                Reset Password
              </UncontrolledTooltip>
              <button
                id={`resetPw-${cellProps.row.original._id}`}
                className="btn btn-sm btn-soft-info reset-list"
                onClick={() => {
                  setResetModal(!resetModal);
                  setData(cellProps.row.original);
                }}
              >
                <i className="mdi mdi-lock-reset align-bottom" />
              </button>

              <UncontrolledTooltip
                target={`delete-${cellProps.row.original._id}`}
              >
                Hapus
              </UncontrolledTooltip>
              <button
                id={`delete-${cellProps.row.original._id}`}
                className="btn btn-sm btn-soft-danger remove-list"
                onClick={() => {
                  setDeleteModal(true);
                  setData(cellProps.row.original);
                }}
              >
                <i className="ri-delete-bin-5-fill align-bottom" />
              </button>
            </div>
          );
        },
      },
    ],
    [],
  );

  useEffect(() => {
    getData();
  }, [pagination, sort, dir, searchName, searchId, filterWorkLocation]);

  useEffect(() => {
    if (showFilter) {
      getComboLocation();
    }
  }, [showFilter]);

  document.title = "Karyawan";

  return (
    <React.Fragment>
      <div className="page-content">
        <Modal
          fade={true}
          isOpen={resetModal}
          toggle={() => setResetModal(false)}
          centered={true}
          size="md"
        >
          <ModalBody className="p-0">
            <button
              type="button"
              className="btn-close position-absolute top-0 end-0 m-3"
              onClick={() => setResetModal(false)}
              style={{ zIndex: 1 }}
            ></button>

            <div className="p-4 pt-5">
              <div className="text-center mb-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "20px",
                    background:
                      "linear-gradient(135deg, #f093fb20 0%, #f5576c20 100%)",
                    border: "3px solid #f5576c30",
                  }}
                >
                  <i
                    className="ri-lock-unlock-line"
                    style={{
                      fontSize: "2.5rem",
                      background:
                        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  ></i>
                </div>

                <h4 className="fw-bold mb-2">Konfirmasi Reset Password</h4>
                <p className="text-muted mb-0">
                  Tindakan ini akan mereset password untuk
                </p>
              </div>

              <div className="card bg-light border-0 mb-3">
                <div className="card-body p-3">
                  <div className="d-flex align-items-center">
                    <div
                      className="avatar-sm me-3"
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="ri-user-line text-white fs-5"></i>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-0">{data?.name}</h6>
                      <small className="text-muted">
                        {data?.email || data?.role}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-warning-subtle border border-warning border-opacity-25 rounded p-3 mb-4">
                <div className="d-flex mb-2">
                  <i className="ri-checkbox-circle-line text-warning me-2 mt-1"></i>
                  <small className="text-muted">
                    Password lama tidak dapat digunakan lagi
                  </small>
                </div>
                <div className="d-flex mb-2">
                  <i className="ri-checkbox-circle-line text-warning me-2 mt-1"></i>
                  <small className="text-muted">
                    Password baru akan digenerate otomatis
                  </small>
                </div>
                <div className="d-flex">
                  <i className="ri-checkbox-circle-line text-warning me-2 mt-1"></i>
                  <small className="text-muted">
                    User harus login ulang dengan password baru
                  </small>
                </div>
              </div>

              <div className="d-grid gap-2">
                <Button
                  color="danger"
                  size="lg"
                  onClick={handleResetPassword}
                  className="fw-medium"
                >
                  <i className="ri-refresh-line me-2"></i>
                  Ya, Reset Password
                </Button>
                <Button
                  color="light"
                  size="lg"
                  onClick={() => setResetModal(false)}
                >
                  Tidak, Batalkan
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal
          fade={true}
          isOpen={passwordModal}
          toggle={() => setPasswordModal(false)}
          centered={true}
          size="md"
        >
          <ModalBody className="py-4 px-4">
            <div className="text-center">
              <div className="mb-3">
                <i className="ri-lock-password-line display-5 text-success"></i>
              </div>
              <h5 className="mb-1">Password Baru Berhasil Dibuat</h5>
              <p className="text-muted mb-0">
                Password untuk <strong>{data?.name}</strong>
              </p>
            </div>

            <div className="mt-4 mb-3">
              <div className="bg-light border border-primary border-opacity-25 rounded-3 p-3">
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <code className="text-primary fs-5 fw-bold flex-grow-1 text-center mb-0">
                    {newPassword}
                  </code>
                  <Button
                    color="primary"
                    size="sm"
                    outline
                    onClick={copyText}
                    className="flex-shrink-0"
                  >
                    <i className="ri-file-copy-line me-1" />
                    Salin
                  </Button>
                </div>
              </div>
            </div>

            <div
              className="alert alert-warning alert-border-left mb-0"
              role="alert"
            >
              <i className="ri-information-line me-2 align-middle"></i>
              <small className="align-middle">
                Pastikan untuk menyimpan password ini dengan aman. Password
                tidak dapat ditampilkan lagi.
              </small>
            </div>

            <div className="d-flex gap-2 justify-content-center mt-4">
              <Button color="primary" onClick={() => setPasswordModal(false)}>
                <i className="ri-check-line me-1" />
                Saya Sudah Menyimpan
              </Button>
            </div>
          </ModalBody>
        </Modal>

        <DataUploadModalMember
          show={uploadDataModal}
          onCloseClick={() => setUploadDataModal(false)}
          getData={() => getData()}
        />

        <PhotoUploadModalMember
          show={photoModal}
          onCloseClick={() => setPhotoModal(false)}
          getData={() => getData()}
        />

        <AddModalMember
          show={addModal}
          onCloseClick={() => setAddModal(false)}
          getData={() => getData()}
        />

        <UpdateModalMember
          show={updateModal}
          onCloseClick={() => setUpdateModal(false)}
          getData={() => getData()}
          data={data}
        />

        <DeleteModalMember
          show={deleteModal}
          onCloseClick={() => setDeleteModal(false)}
          getData={() => getData()}
          data={data}
          setData={() => setData({})}
        />

        <Container fluid>
          <BreadCrumb title="Data Karyawan" pageTitle="Karyawan" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <div className="d-flex align-items-center">
                    <h5 className="card-title mb-0 flex-grow-1">Karyawan</h5>
                    <div className="flex-shrink-0">
                      <div className="d-flex gap-2 flex-wrap">
                        <button
                          className="btn btn-soft-primary btn-sm"
                          onClick={handleToggleFilter}
                        >
                          <i className="ri-filter-3-line align-bottom me-1"></i>
                          Filters
                        </button>
                        <button
                          className="btn btn-soft-dark btn-sm"
                          onClick={() => downloadData()}
                        >
                          {isLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Menunggu...
                            </>
                          ) : (
                            <>
                              <i className="ri-file-download-line align-bottom me-1"></i>
                              Unduh Data Karyawan
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-soft-info btn-sm"
                          onClick={() => setPhotoModal(!photoModal)}
                        >
                          <i className="ri-image-add-line align-bottom me-1"></i>
                          Upload Foto Karyawan
                        </button>
                        <button
                          className="btn btn-soft-secondary btn-sm"
                          onClick={() => setUploadDataModal(!uploadDataModal)}
                        >
                          <i className="ri-file-upload-line align-bottom me-1"></i>
                          Upload Data Karyawan
                        </button>
                        <div
                          className="btn btn-soft-success btn-sm"
                          onClick={() => {
                            setAddModal(true);
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          Tambah
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="pt-0">
                  <Collapse isOpen={showFilter}>
                    <Row className="g-3 mb-4">
                      <Col lg={4}>
                        <div>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Cari nama karyawan..."
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                          />
                        </div>
                      </Col>
                      {/* <Col lg={4}>
                        <div>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Cari No Reg karyawan..."
                            value={filterId}
                            onChange={(e) => setFilterId(e.target.value)}
                          />
                        </div>
                      </Col> */}
                      <Col lg={3}>
                        <Select
                          value={filterWorkLocation}
                          onChange={(e: any) => {
                            setFilterWorkLocation(e);
                          }}
                          options={comboLocation}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          placeholder="Cari Lokasi Kerja"
                          isClearable
                        />
                      </Col>
                      <Col lg={12}>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-soft-secondary btn-sm"
                            onClick={handleResetFilter}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            Reset
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Collapse>
                  <div>
                    {loading ? (
                      <Loader />
                    ) : (
                      <TableContainer
                        columns={columns}
                        data={tableData || []}
                        customPageSize={10}
                        theadClass="text-muted text-uppercase"
                        manualPagination
                        pagination={pagination}
                        pageCount={totalPages}
                        onPaginationChange={setPagination}
                        divClass="table-responsive"
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MemberList;
